import { format, add, sub, getDaysInMonth, lastDayOfMonth, isLastDayOfMonth, isToday } from 'date-fns';
import { durationMap } from '../constants';
import {
	IDate,
	IOffer,
	IOrderInfoDetails,
	IVignetteOffer,
	Locales,
	IFormatCheckoutVignetteDateTime,
	ILabel,
	IDowntime,
} from '../models';
import { getLabelValue } from './contentfulHelpers';

export const getTimeFormat = (lang?: Locales) => (lang === 'en' ? 'hh:mm a' : 'HH:mm');
export const getGermanSuffix = (lang?: Locales) => (lang === 'de' ? ' Uhr' : '');
export const getDateTimeFormat = (date: Date, formatString: string): string => format(date, formatString);

export const getDateString = (date: Date) => {
	try {
		return format(date, 'yyyy-MM-dd');
	} catch (error) {
		return '';
	}
};

export const getTimeString = (date: Date) => {
	try {
		return format(date, 'HH:mm');
	} catch (error) {
		return '';
	}
};

export const formatDate = (date?: Date) => {
	try {
		return date ? format(date, 'dd.MM.yyyy') : '';
	} catch (error) {
		return '';
	}
};

export const convertApiDate = (date: IDate): Date => new Date(`${date.date}T${date.time}:00`);

// Ensuring to always have the default behaviour of the DatePicker
// Changing dates with the keyboard also change the hour and affects the end date calculation
// Whereas changing dates with the mouse doesn't change the hour
export const calculateNewStartDate = (startDate: Date) => {
	const resettedStartDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0);
	return resettedStartDate;
};

export const calculateEndDate = (startDate: Date, selectedVignette: IVignetteOffer | IOrderInfoDetails): Date => {
	const isSectionToll = selectedVignette.moduleTypeId === 3;
	if (isSectionToll) {
		return sub(add(startDate, durationMap.Y), { seconds: 1 });
	}
	const duration = Object.keys(durationMap).find((key) => selectedVignette.serviceCatalogCode.endsWith(key));
	const endDate = sub(add(startDate, durationMap[duration || '']), { seconds: 1 });

	// Schweiz special case
	if (selectedVignette.supplierId === 14) {
		return selectedVignette.usageTo ? convertApiDate(selectedVignette.usageTo) : endDate;
	}

	// needs to be clarified, when this logic is needed exactly; currently not relevant for Asfinag; see comments Ticket https://mdtravel.atlassian.net/browse/AMRMAUT-330
	// const usageTo = selectedVignette.usageTo ? convertApiDate(selectedVignette.usageTo) : undefined;
	// if (usageTo && endDate.getTime() > usageTo.getTime()) {
	// 	return usageTo;
	// }

	return endDate;
};

export const getDaysOfTheNextMonth = (currDate: Date) => {
	if (currDate.getMonth() === 11) return getDaysInMonth(new Date(currDate.getFullYear(), 0));
	return getDaysInMonth(new Date(currDate.getFullYear(), currDate.getMonth() + 1));
};

export const setEndDate = (startDate: Date, selectedVignette: IVignetteOffer | IOrderInfoDetails) => {
	//covering special case for the SLO monthly vignette
	if (
		selectedVignette.serviceCatalogCode.includes('1M') &&
		isLastDayOfMonth(startDate) &&
		getDaysInMonth(startDate) > getDaysOfTheNextMonth(startDate)
	) {
		return lastDayOfMonth(new Date(startDate.getFullYear(), startDate.getMonth() + 1));
	}
	return calculateEndDate(startDate, selectedVignette);
};

export const calculateNewDates = (start: Date, selectedVignette?: IVignetteOffer | IOrderInfoDetails) => {
	const newStartDate = calculateNewStartDate(start);
	if (selectedVignette) {
		// Streckenmaut/Tollsection special case
		if (selectedVignette.moduleTypeId === 3) {
			return {
				start: newStartDate,
				end: newStartDate,
			};
		}
		const newEndDate = setEndDate(newStartDate, selectedVignette);
		const updatedDates = {
			start: newStartDate,
			end: newEndDate,
		};
		return updatedDates;
	}
	return undefined;
};

export const checkForMaintenance = (offers: IOffer[]): boolean => {
	const downtime = offers.find(({ downtimes }) => downtimes.length > 0)?.downtimes;
	if (downtime && downtime[0].type === 'stop') {
		const { usageFrom, usageTo } = downtime[0];
		const currentTime = new Date().getTime();
		const maintenanceStart = convertApiDate(usageFrom).getTime();
		const maintenanceEnd = convertApiDate(usageTo).getTime();
		if (currentTime >= maintenanceStart && currentTime <= maintenanceEnd) {
			return true;
		}
	}
	return false;
};

export const formatCheckoutVignetteDateTime = ({
	date,
	datesAreEqual,
	withTime,
	lang = 'de',
	fullDateTimeFormat,
	labels,
}: IFormatCheckoutVignetteDateTime) => {
	if (isToday(date) && !datesAreEqual && !fullDateTimeFormat)
		return getLabelValue('mp.immediately', labels) || 'Sofort';

	if (datesAreEqual) {
		return `${getDateTimeFormat(
			date,
			`${fullDateTimeFormat ? 'dd.MM.yyyy, ' : ''}${getTimeFormat(lang)}`,
		)}${getGermanSuffix(lang)}`;
	}

	return `${getDateTimeFormat(
		date,
		`${fullDateTimeFormat || withTime ? `dd.MM.yyyy, ${getTimeFormat(lang)}` : 'dd.MM.yyyy'}`,
	)}${fullDateTimeFormat || withTime ? getGermanSuffix(lang) : ''}`;
};

export const formatDateTimeValues = (
	checkOutDateTime: IDate,
	checkInDateTime: IDate,
	lang: Locales = 'de',
	fullDateTimeFormat = false,
	labels?: ILabel[],
) => {
	const datesAreEqual = checkInDateTime.date === checkOutDateTime.date;
	const checkOutDateGermanFormat = formatCheckoutVignetteDateTime({
		date: convertApiDate(checkOutDateTime),
		datesAreEqual: false,
		withTime: datesAreEqual,
		lang,
		fullDateTimeFormat,
		labels,
	});
	const checkInDateGermanFormat = formatCheckoutVignetteDateTime({
		date: convertApiDate(checkInDateTime),
		datesAreEqual,
		withTime: true,
		lang,
		fullDateTimeFormat,
		labels,
	});
	return `${checkOutDateGermanFormat} – ${checkInDateGermanFormat}`;
};

export const tollSectionIsDisabled = (downtimes: IDowntime[]) => {
	const today = new Date().getTime();
	return downtimes
		? downtimes[0]?.type === 'warn' &&
				convertApiDate(downtimes[0]?.usageFrom).getTime() <= today &&
				convertApiDate(downtimes[0]?.usageTo).getTime() >= today
		: false;
};
